import type {
  QueryKey,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { Asset } from '@Types/assets';
import service from '../service';
import { stableStringify } from '../utils/searchParams';

type AssetsResponse = Asset[];
type DefaultError = unknown;

type UseAssetsParams<S extends Record<string, any> = Record<string, any>> = {
  /** Optional query string params to forward to service.getAssets */
  searchParams?: S;
  /** Override default enabled behavior */
  enabled?: boolean;
  /** Tag names to exclude from results. Defaults to ['Member Profile']. Pass [] to include all. */
  excludeTags?: string[];
};

export const assetsKeys = {
  all: ['assets'] as const,
  list: (searchParams?: Record<string, any>) =>
    ['assets', stableStringify(searchParams)] as const,
};

/**
 * useVenue
 *
 * @example
  const venueQuery = useVenue(
    {
      venueId,
      searchParams: {
        include: 'spaces',
        toastInclude: 'toastRestaurant,toastPrivateDiningMenu',
        }
     },
    { staleTime: 60_000 }
  );
 */
const DEFAULT_EXCLUDED_TAGS = ['Member Profile'];

export function useAssets<TData = AssetsResponse, TError = DefaultError>(
  {
    searchParams,
    enabled = true,
    excludeTags = DEFAULT_EXCLUDED_TAGS,
  }: UseAssetsParams = {},
  options?: Omit<
    UseQueryOptions<AssetsResponse, TError, TData, QueryKey>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
): UseQueryResult<TData, TError> {
  return useQuery<AssetsResponse, TError, TData>({
    queryKey: assetsKeys.list(searchParams),
    enabled: enabled,
    queryFn: async () => {
      return service.getAssets({
        sort: '-createdAt',
        include: 'tags.tag',
        ...(searchParams ?? {}),
      });
    },
    select: (data) => {
      if (!excludeTags || excludeTags.length === 0)
        return data as unknown as TData;

      const excluded = new Set(excludeTags.map((t) => t.toLowerCase()));
      const filtered = data.filter((asset) => {
        const tagNames =
          (asset as any).tags?.map((t: any) => t.tag?.name?.toLowerCase()) ??
          [];
        return !tagNames.some((name: string) => excluded.has(name));
      });

      return filtered as unknown as TData;
    },
    ...(options as any),
  });
}
